import { QUERY_KEYS } from "@/common/query-keys";
import { useSettings } from "@/context/settings-context";
import { fetchAllConsumables } from "@/lib/snipeit/consumables";
import type { SnipeItConsumable } from "@/lib/snipeit/types";
import { useQuery } from "@tanstack/react-query";

export interface LowStockConsumable extends SnipeItConsumable {
  /** How far below the minimum: min_amt - remaining (positive = below minimum) */
  deficit: number;
}

/** Pure filter/sort logic – extracted for testability. */
export function filterLowStockConsumables(
  consumables: SnipeItConsumable[],
  blacklistIds: Set<number>,
): LowStockConsumable[] {
  return consumables
    .filter((c) => c.min_amt > 0 && c.remaining <= c.min_amt + 5 && !blacklistIds.has(c.id))
    .map((c) => ({ ...c, deficit: c.min_amt - c.remaining }))
    .sort((a, b) => b.deficit / b.min_amt - a.deficit / a.min_amt);
}

/**
 * Fetches all consumables whose `remaining` is at or below `min_amt`,
 * excluding any IDs on the consumable blacklist.
 * Sorted by deficit descending (most critical first).
 */
export function useLowStockConsumables() {
  const { consumableBlacklist } = useSettings();
  const blacklistSet = new Set(consumableBlacklist.map((b) => b.id));

  const query = useQuery({
    queryKey: [QUERY_KEYS.consumablesLowStock],
    queryFn: fetchAllConsumables,
    refetchInterval: 60_000,
    staleTime: 30_000,
  });

  const items = filterLowStockConsumables(query.data ?? [], blacklistSet);

  return { items, isLoading: query.isLoading };
}
